import { useLazyQuery } from '@apollo/react-hooks';
import { Collapse } from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import classNames from 'classnames';
import { format } from 'date-fns';
import PropTypes from 'prop-types';
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Trans, useTranslation } from 'react-i18next';
import Button from '~components/button';
import ConfirmationTippy from '~components/confirmation-tippy';
import parseValueToDate from '~components/date-picker/helpers/parse-value-to-date';
import { DATE_FORMAT, SPLITTER } from '~components/date-picker/range-day-picker-input';
import FormikSelect from '~components/select/formik-select';
import Typography from '~components/typography';
import {
  QUERY_GET_RATE_TYPE,
  QUERY_NODE_TENANCY_OPTION,
  QUERY_ROOM_TYPE_AVAILABILITY_WITH_TENANCY_OPTION_OR_DATE,
} from '~containers/booking/gql';
import BookingFormTenancy from '~containers/creating-booking/booking-form-tenancy';
import { BOOKING_CATEGORY, BOOKING_STATUS, CURRENCY_SYMBOL, TENANCY_TYPE } from '~graphql-types';
import { BasicAcademicYearFilled, SettingsRoomtype } from '~svg-components';
import useHandleError from '~utils/use-handle-error';
import { TENANCY_CUSTOMIZED } from './constants';
import PropertySelect from './property-select';
import TenancyCustomizedForm from './tenancy-customized-form';

const useRoomTypeSelectStyles = makeStyles((theme) => ({
  roomTypeBeds: {
    marginLeft: '8px',
  },

  roomTypeBedsUrgency: {
    color: theme.colors.orangeDark,
  },

  roomTypeDisabled: {
    color: theme.colors.greyCc,
  },
}));

const sortRoomTypeInfosByName = (a, b) => {
  if (a === b || a.roomType.name === b.roomType.name) {
    return 0;
  }
  if (a.roomType.name < b.roomType.name) {
    return -1;
  }
  return 1;
};

const useGetRoomTypeSelectOptions = ({ showNoPriceRoomTypes }) => {
  const { t } = useTranslation('booking');
  const classes = useRoomTypeSelectStyles();

  return useCallback(
    (inventoryThresholdForUrgencyMessage, roomTypeInfos) => {
      const noPriceRoomTypes = [];
      const soldOutRoomTypes = [];
      const normalRoomTypes = [];

      roomTypeInfos.forEach((roomTypeInfo) => {
        const {
          numberOfAvailableBeds,
          minPrice: { minPriceForBillingCycle },
        } = roomTypeInfo;

        if (minPriceForBillingCycle === null && !showNoPriceRoomTypes) {
          noPriceRoomTypes.push(roomTypeInfo);
        } else if (numberOfAvailableBeds === 0) {
          soldOutRoomTypes.push(roomTypeInfo);
        } else {
          normalRoomTypes.push(roomTypeInfo);
        }
      });

      normalRoomTypes.sort(sortRoomTypeInfosByName);
      noPriceRoomTypes.sort(sortRoomTypeInfosByName);
      soldOutRoomTypes.sort(sortRoomTypeInfosByName);

      const normalRoomTypeOptions = normalRoomTypes.map((roomTypeInfo) => ({
        value: roomTypeInfo.roomType.id,
        disabled: false,
        children: (
          <div>
            {roomTypeInfo.roomType.name}
            <Typography
              component="span"
              variant="body2"
              themeColor="grey99"
              className={classNames(
                classes.roomTypeBeds,
                inventoryThresholdForUrgencyMessage > 0 &&
                  roomTypeInfo.numberOfAvailableBeds < inventoryThresholdForUrgencyMessage &&
                  classes.roomTypeBedsUrgency
              )}
            >
              {t('booking.creating.room_type.beds', { beds: roomTypeInfo.numberOfAvailableBeds })}
            </Typography>
          </div>
        ),
      }));

      const noPriceRoomTypeOptions = noPriceRoomTypes.map((roomTypeInfo) => ({
        value: roomTypeInfo.roomType.id,
        disabled: true,
        children: (
          <div className={classes.roomTypeDisabled}>
            {roomTypeInfo.roomType.name}
            {t('booking.creating.room_type.no_price')}
          </div>
        ),
      }));

      const soldOutRoomTypeOptions = soldOutRoomTypes.map((roomTypeInfo) => ({
        value: roomTypeInfo.roomType.id,
        disabled: true,
        children: (
          <div className={classes.roomTypeDisabled}>
            {roomTypeInfo.roomType.name}
            {t('booking.creating.room_type.sold_out')}
          </div>
        ),
      }));

      return normalRoomTypeOptions.concat(noPriceRoomTypeOptions).concat(soldOutRoomTypeOptions);
    },
    [
      classes.roomTypeBeds,
      classes.roomTypeBedsUrgency,
      classes.roomTypeDisabled,
      showNoPriceRoomTypes,
      t,
    ]
  );
};

const useStyles = makeStyles(
  (theme) => ({
    root: {
      position: 'relative',
      width: '560px',
    },

    bookingFormWrapper: {
      display: 'flex',
      alignItems: 'center',
      flexDirection: 'column',
      paddingBottom: '56px',
    },

    bookingFormWrapperSmaller: {
      paddingBottom: '40px',
    },

    bookingForm: {
      display: 'flex',
      alignItems: 'flex-start',
      flexDirection: 'column',
    },

    actions: {
      padding: '12px 16px 12px 0',
      justifyContent: 'flex-end',
    },

    title: {
      marginTop: '18px',
    },

    subtitle: {
      marginTop: '8px',
      marginBottom: '12px',
      width: 320,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },

    field: {
      marginTop: '20px',
      width: '320px',
      height: '48px',

      '&.Mui-error $fieldIcon': {
        color: theme.colors.red,
      },
    },

    fieldIcon: {
      marginLeft: '16px',
      color: theme.colors.grey66,
    },

    createButton: {
      marginTop: '32px',
      width: '320px',
      height: '48px',
    },

    price: {
      marginTop: '8px',
    },

    bookingConfirm: {
      width: '320px',
    },
    propertySelect: {
      display: 'inline-block',
      marginLeft: 12,
    },
    fullName: {
      display: 'inline-block',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      textAlign: 'right',
    },
    divide: {
      height: 16,
      width: 1,
      backgroundColor: theme.colors.greyCc,
      marginLeft: 12,
    },
  }),
  { name: 'NewBookingModal' }
);

const isValidDateRange = (dateRange) => !!(dateRange ? dateRange.split(SPLITTER)[1] : false);

const getActiveBookingsStatus = (bookings) => {
  if (!bookings) {
    return [];
  }
  return bookings.filter(
    ({ status }) =>
      ![
        BOOKING_STATUS.RESIDENT,
        BOOKING_STATUS.PAST,
        BOOKING_STATUS.EXPIRED,
        BOOKING_STATUS.CANCELLED,
      ].includes(status)
  );
};

const BookingForm = forwardRef(
  (
    {
      academicYearList,
      bookingCategory = BOOKING_CATEGORY.DIRECT_LET,
      currentProperty,
      edit,
      formik,
      getAcademicYearsLoading,
      nominationPlanId,
      onPropertyChange,
      showNoPriceRoomTypes,
      isPayToPartner,
      student,
    },
    ref
  ) => {
    const { t } = useTranslation('booking');
    const classes = useStyles();

    const studentActiveBookingsCount = useMemo(() => getActiveBookingsStatus(student.bookings), [
      student.bookings,
    ]);
    const { academicYear } = currentProperty;
    const { values, setValues, setTouched } = formik;
    const [tenancyOptions, setTenancyOptions] = useState(null);
    const [roomTypes, setRoomTypes] = useState(null);
    const [rateTypeOnPropertySetting, setRateTypeOnPropertySetting] = useState(null);
    const [showCustomizedTenancy, setShowCustomizedTenancy] = useState(false);
    const setValuesWithAutoClearRef = useRef(() => {});

    // formik.values will be updated every time when setValues invoked
    // so I use ref to get up-to-date values here
    // note: useCallback had weird issue with values
    setValuesWithAutoClearRef.current = (fields) => {
      // important: the order is fixed
      const orderedFields = [
        'academic_year',
        'tenancy_option',
        'date_range',
        'room_type',
        'free_cancellation_deadline_date',
        'interval_for_turnaround',
      ];
      const isDifferent = Object.keys(fields).some((f) => fields[f] !== values[f]);
      if (isDifferent) {
        const getEmptyChildren = (field) => {
          const emptyChildren = {};
          const index = orderedFields.indexOf(field);
          if (index === -1) {
            return emptyChildren;
          }
          orderedFields.slice(index + 1).forEach((orderedField) => {
            emptyChildren[orderedField] = '';
          });
          return emptyChildren;
        };

        let newValues = values;
        Object.keys(fields).forEach((field) => {
          newValues = {
            ...newValues,
            [field]: fields[field],
            ...getEmptyChildren(field),
          };
        });

        const newTouched = {};
        Object.keys(newValues).forEach((field) => {
          newTouched[field] = !!newValues[field] || newValues[field] === 0;
        });
        setValues(newValues);
        setTouched(newTouched);
      }
    };

    const setAcademicYear = (value) => {
      setValuesWithAutoClearRef.current({
        academic_year: value,
      });
    };

    const currentAcademicYear = useMemo(
      () =>
        values.academic_year
          ? Object.values(academicYear).find((v) => v?.id === values.academic_year)
          : undefined,
      [values.academic_year, academicYear]
    );

    const currentTenancyOption = useMemo(() => {
      if (values.tenancy_option && tenancyOptions) {
        return tenancyOptions.tenancyOptions.find((item) => item.id === values.tenancy_option);
      }
      return null;
    }, [values.tenancy_option, tenancyOptions]);

    const currentRoomType = useMemo(() => {
      if (values.room_type && roomTypes) {
        return roomTypes.find((item) => item.roomType.id === values.room_type);
      }
      return null;
    }, [values.room_type, roomTypes]);

    const [
      getTenancyOptions,
      { error: tenancyOptionError, loading: tenancyOptionLoading },
    ] = useLazyQuery(QUERY_NODE_TENANCY_OPTION, {
      fetchPolicy: 'no-cache',
      onCompleted: (resp) => {
        setTenancyOptions(resp.node || []);
      },
    });
    useHandleError(tenancyOptionError);

    const [
      queryGetRateType,
      { error: queryGetRateTypeError, loading: queryGetRateTypeLoading },
    ] = useLazyQuery(QUERY_GET_RATE_TYPE, {
      fetchPolicy: 'no-cache',
      onCompleted: (resp) => {
        setRateTypeOnPropertySetting(resp?.getFlexibilityControls?.setUpRateType?.value);
      },
    });
    useHandleError(queryGetRateTypeError);

    // Load tenancy option
    useEffect(() => {
      if (values.academic_year) {
        setTenancyOptions(null);
        getTenancyOptions({
          variables: {
            id: currentProperty.id,
            academicYearId: values.academic_year,
            isValid: true,
            bookingCategory,
            nominationPlanId,
          },
        });

        setRateTypeOnPropertySetting(null);
        queryGetRateType({
          variables: {
            propertyId: currentProperty.id,
            academicYearId: values.academic_year,
          },
        });
      }
    }, [
      getTenancyOptions,
      setTenancyOptions,
      currentProperty.id,
      values.academic_year,
      bookingCategory,
      nominationPlanId,
      queryGetRateType,
    ]);

    const [
      getRoomTypesByIdOrDate,
      { error: roomTypeError, loading: roomTypeLoading },
    ] = useLazyQuery(QUERY_ROOM_TYPE_AVAILABILITY_WITH_TENANCY_OPTION_OR_DATE, {
      fetchPolicy: 'no-cache',
      onCompleted: (resp) => {
        setRoomTypes(resp?.getRoomTypeAvailabilityWithTenancyOptionOrDate?.roomTypeInfos || {});
      },
    });
    useHandleError(roomTypeError);

    // Load room type by id
    useEffect(() => {
      if (
        values.tenancy_option &&
        values.tenancy_option !== TENANCY_CUSTOMIZED &&
        currentTenancyOption
      ) {
        setRoomTypes(null);
        const variables = {
          propertyId: currentProperty.id,
          filters: {
            academicYearId: values.academic_year,
            tenancyOptionId: values.tenancy_option,
            tenancyOptionRateType: currentTenancyOption.rateType,
            nominationPlanId,
          },
        };
        if (
          currentTenancyOption.tenancyType === TENANCY_TYPE.FLEXIBLE &&
          isValidDateRange(values.date_range)
        ) {
          const [start, end] = values.date_range.split(SPLITTER);
          variables.filters.dateStart = format(parseValueToDate(start, DATE_FORMAT), 'yyyy-MM-dd');
          variables.filters.dateEnd = format(parseValueToDate(end, DATE_FORMAT), 'yyyy-MM-dd');
          getRoomTypesByIdOrDate({ variables });
        } else if (currentTenancyOption.tenancyType === TENANCY_TYPE.FIXED) {
          getRoomTypesByIdOrDate({ variables });
        }
      }
    }, [
      currentProperty.id,
      values.academic_year,
      values.tenancy_option,
      values.date_range,
      getRoomTypesByIdOrDate,
      setRoomTypes,
      currentTenancyOption,
      nominationPlanId,
    ]);

    // Load room type by date range
    useEffect(() => {
      if (values.tenancy_option === TENANCY_CUSTOMIZED && isValidDateRange(values.date_range)) {
        setRoomTypes(null);
        const [start, end] = values.date_range.split(SPLITTER);
        getRoomTypesByIdOrDate({
          variables: {
            propertyId: currentProperty.id,
            filters: {
              academicYearId: values.academic_year,
              tenancyOptionRateType: values.rate_type,
              dateStart: format(parseValueToDate(start, DATE_FORMAT), 'yyyy-MM-dd'),
              dateEnd: format(parseValueToDate(end, DATE_FORMAT), 'yyyy-MM-dd'),
              intervalForTurnaround: values.interval_for_turnaround || 0,
              nominationPlanId,
            },
          },
        });
      }
    }, [
      currentProperty.id,
      values.academic_year,
      values.tenancy_option,
      values.date_range,
      values.interval_for_turnaround,
      getRoomTypesByIdOrDate,
      setRoomTypes,
      nominationPlanId,
      values.rate_type,
    ]);

    const getRoomTypeSelectOptions = useGetRoomTypeSelectOptions({ showNoPriceRoomTypes });
    const roomTypeList = useMemo(
      () =>
        roomTypes
          ? getRoomTypeSelectOptions(
              currentAcademicYear?.flexibilityControlInfo?.inventoryThresholdForUrgencyMessage,
              roomTypes
            )
          : [],
      [
        getRoomTypeSelectOptions,
        currentAcademicYear?.flexibilityControlInfo?.inventoryThresholdForUrgencyMessage,
        roomTypes,
      ]
    );

    const onSubmit = (e) => {
      e.preventDefault();
      if (!formik.isSubmitting) {
        formik.handleSubmit();
      }
    };

    let isBeyondBookingLimit;
    if (!edit && !formik.isSubmitting && formik.isValid) {
      isBeyondBookingLimit =
        currentAcademicYear?.flexibilityControlInfo?.maxRequestsForEachStudent <=
        studentActiveBookingsCount;
    }

    const price = `${CURRENCY_SYMBOL[currentProperty?.currency]}${
      currentRoomType?.minPrice?.minPriceForBillingCycle
    }`;

    const isFlexibleWithoutDate =
      currentTenancyOption?.tenancyType === TENANCY_TYPE.FLEXIBLE &&
      !isValidDateRange(values.date_range);

    const isRoomTypeDisabled =
      !values.tenancy_option ||
      tenancyOptionLoading ||
      roomTypeLoading ||
      isFlexibleWithoutDate ||
      queryGetRateTypeLoading;

    const getCustomizedInitialValues = () => {
      if (values.tenancy_option === TENANCY_CUSTOMIZED) {
        return {
          date_range: values.date_range,
          free_cancellation_deadline_date: values.free_cancellation_deadline_date,
          interval_for_turnaround: values.interval_for_turnaround,
          rate_type: values.rate_type,
          isDifferentChargingDate: values.isDifferentChargingDate,
          charging_date_range: values.charging_date_range,
        };
      }
      return {
        date_range: '',
        free_cancellation_deadline_date: '',
        interval_for_turnaround: '',
        rate_type: 'NIGHTLY',
      };
    };

    const onConfirmCustomizedTenancy = (fields) => {
      setValuesWithAutoClearRef.current({
        tenancy_option: TENANCY_CUSTOMIZED,
        date_range: fields.date_range,
        free_cancellation_deadline_date: fields.free_cancellation_deadline_date,
        interval_for_turnaround: fields.interval_for_turnaround,
        rate_type: fields.rate_type,
        isDifferentChargingDate: fields.isDifferentChargingDate,
        charging_date_range: fields.charging_date_range,
      });
      setShowCustomizedTenancy(false);
      formik.setFieldValue('tenancy_option', TENANCY_CUSTOMIZED);
    };

    useImperativeHandle(ref, () => ({ tenancyOptions }));
    return (
      <div className={classes.root}>
        <div
          className={classNames(classes.bookingFormWrapper, {
            [classes.bookingFormWrapperSmaller]: !!currentRoomType && !isPayToPartner,
          })}
        >
          <Typography variant="h2" themeColor="dark" align="center" className={classes.title}>
            {edit ? t('booking.creating.edit') : t('booking.creating.new')}
          </Typography>

          <Typography
            variant="body1"
            component="div"
            themeColor="grey66"
            align="center"
            className={classes.subtitle}
          >
            <Typography
              variant="body1"
              component="div"
              themeColor="grey66"
              align="center"
              className={classes.fullName}
            >
              {student?.fullName}
            </Typography>
            <div className={classes.divide}></div>
            <Typography
              variant="body1"
              component="div"
              themeColor="grey66"
              align="center"
              className={classes.propertySelect}
              style={{ cursor: edit ? 'default' : 'pointer' }}
            >
              <PropertySelect
                isDisabled={edit}
                property={currentProperty}
                onChange={onPropertyChange}
              />
            </Typography>
          </Typography>
          <form onSubmit={onSubmit} className={classes.bookingForm}>
            <FormikSelect
              className={classes.field}
              placeholder={t('booking.creating.form.academic_year')}
              fieldConfig="academic_year"
              options={academicYearList}
              loading={getAcademicYearsLoading}
              InputProps={{
                startAdornment: <BasicAcademicYearFilled className={classes.fieldIcon} />,
              }}
              onChange={(event, item) => {
                setAcademicYear(item.value);
              }}
              disabled={nominationPlanId}
            />

            <BookingFormTenancy
              fieldClassName={classes.field}
              formik={formik}
              currentProperty={currentProperty}
              loading={tenancyOptionLoading || queryGetRateTypeLoading}
              tenancyOptions={tenancyOptions}
              currentTenancyOption={currentTenancyOption}
              onClickCustomizedTenancyOption={() => setShowCustomizedTenancy(true)}
              setValuesWithAutoClearRef={setValuesWithAutoClearRef}
              hasCustomizeOption={!nominationPlanId}
            />

            <FormikSelect
              className={classes.field}
              placeholder={t('booking.creating.form.room_type')}
              fieldConfig="room_type"
              options={roomTypeList}
              loading={roomTypeLoading}
              disabled={isRoomTypeDisabled}
              InputProps={{
                startAdornment: <SettingsRoomtype className={classes.fieldIcon} />,
              }}
              renderValue={() => currentRoomType?.roomType?.name || ''}
            />

            {isBeyondBookingLimit ? (
              <ConfirmationTippy
                className={classes.bookingConfirm}
                type="default"
                content={t('booking.creating.max_confirm.description', {
                  number: studentActiveBookingsCount,
                })}
                confirmButtonProps={{ children: t('booking.creating.max_confirm.yes') }}
                onConfirm={formik.handleSubmit}
                placement="top"
              >
                <Button
                  type="button"
                  key="confirm"
                  color="primary"
                  className={classes.createButton}
                  loading={formik.isSubmitting}
                >
                  {edit ? t('button.save_changes') : t('booking.creating.student.create_booking')}
                </Button>
              </ConfirmationTippy>
            ) : (
              <Button
                type="submit"
                key="confirm"
                color="primary"
                className={classes.createButton}
                loading={formik.isSubmitting}
              >
                {edit ? t('button.save_changes') : t('booking.creating.student.create_booking')}
              </Button>
            )}
          </form>

          {!isPayToPartner && (
            <Collapse in={!!currentRoomType} mountOnEnter unmountOnExit>
              <Typography
                variant="body2"
                themeColor="grey66"
                align="center"
                className={classes.price}
              >
                <Trans
                  ns="booking"
                  i18nKey={`booking.creating.room_type.price.${currentProperty?.billingCycle}`}
                >
                  From
                  <Typography component="span" variant="h5" themeColor="dark">
                    {{ price }}
                  </Typography>
                  / day
                </Trans>
                {currentRoomType?.rollingRateValue && (
                  <Trans ns="booking" i18nKey={'rolling_rate_with_percentage_and_label'}>
                    From
                    <Typography component="span" variant="h5" themeColor="dark">
                      {{ rollingRateValue: `${parseFloat(currentRoomType?.rollingRateValue)}%` }}
                    </Typography>
                  </Trans>
                )}
              </Typography>
            </Collapse>
          )}
        </div>
        {showCustomizedTenancy && (
          <TenancyCustomizedForm
            currentAcademicYear={currentAcademicYear}
            data={getCustomizedInitialValues()}
            onConfirm={onConfirmCustomizedTenancy}
            onClose={() => setShowCustomizedTenancy(false)}
            rateTypeOnPropertySetting={rateTypeOnPropertySetting}
          />
        )}
      </div>
    );
  }
);

BookingForm.propTypes = {
  academicYearList: PropTypes.array,
  bookingCategory: PropTypes.string,
  currentProperty: PropTypes.object.isRequired,
  edit: PropTypes.bool,
  formik: PropTypes.object.isRequired,
  getAcademicYearsLoading: PropTypes.bool,
  nominationPlanId: PropTypes.string,
  onPropertyChange: PropTypes.func.isRequired,
  student: PropTypes.object.isRequired,
  showNoPriceRoomTypes: PropTypes.bool,
  isPayToPartner: PropTypes.bool,
};

export default BookingForm;
